import React, { useCallback, useEffect, useState } from 'react';
import { vendorService } from '../../services';
import { ClubPanel, PanelHead, MUTED, RULE } from '../club/clubUi';
import ProEventPicker from './ProEventPicker';

/**
 * usePageEvents — the linked list behind EVENTS ON YOUR PAGE, shared by the dashboard
 * panel and the venue wizard step. `gone` is true when the endpoint answers 404 (the API
 * has not landed / the page is not one the caller manages) so the block can hide itself.
 * `onLinked` upserts by id; `onUnlinked` removes — the picker's optimistic contract.
 */
export function usePageEvents(pageId) {
  const [linked, setLinked] = useState([]);
  const [loading, setLoading] = useState(!!pageId);
  const [gone, setGone] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!pageId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const r = await vendorService.pageEvents(pageId);
    setLoading(false);
    if (r.success) { setLinked(r.data?.items || r.data || []); return; }
    if (r.status === 404) { setGone(true); return; }
    setError(r.error || 'Could not load the events on this page.');
  }, [pageId]);
  useEffect(() => { reload(); }, [reload]);

  const onLinked = useCallback((row) => setLinked((prev) => (prev.some((x) => x.id === row.id) ? prev.map((x) => (x.id === row.id ? { ...x, ...row } : x)) : [...prev, row])), []);
  const onUnlinked = useCallback((id) => setLinked((prev) => prev.filter((x) => x.id !== id)), []);

  return { linked, loading, gone, error, reload, onLinked, onUnlinked };
}

/**
 * PageEventsPanel — the dashboard's PAGE tab block, on the Portal vocabulary. Any kind.
 * Hidden entirely when the endpoint is not there (404); a plain error line with RETRY otherwise.
 */
export default function PageEventsPanel({ pageId, phone = false }) {
  const { linked, loading, gone, error, reload, onLinked, onUnlinked } = usePageEvents(pageId);
  if (!pageId || gone) return null;
  return (
    <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="page-events-panel">
      <PanelHead title="EVENTS ON YOUR PAGE" right={loading ? 'LOADING…' : `${linked.length} LINKED`} />
      {error ? (
        <div className="flex items-center justify-between font-mono" style={{ gap: 12, fontSize: 12, color: MUTED }}>
          <span>{error}</span>
          <button type="button" onClick={reload} className="font-mono font-bold uppercase underline hover:no-underline" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#000' }}>RETRY</button>
        </div>
      ) : (
        <ProEventPicker pageId={pageId} linked={linked} onLinked={onLinked} onUnlinked={onUnlinked} />
      )}
      <span style={{ paddingTop: 10, borderTop: RULE, fontSize: 11, lineHeight: 1.6, color: MUTED }}>Anything linked here shows on your page&apos;s EVENTS tab. Events you host can also be added from the event builder.</span>
    </ClubPanel>
  );
}
